import React, { useState } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Divider, Grid, Card,
  CardContent, List, ListItem, ListItemIcon, ListItemText, Container,
  Dialog, DialogTitle, DialogContent, DialogActions, useTheme, useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import BookIcon from '@mui/icons-material/Book';
import LabelIcon from '@mui/icons-material/Label';
import ExtensionIcon from '@mui/icons-material/Extension';
import SettingsIcon from '@mui/icons-material/Settings';
import SchoolIcon from '@mui/icons-material/School';
import EmailIcon from '@mui/icons-material/Email';
import CalculateIcon from '@mui/icons-material/Calculate';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CreateTicketModal from '../components/CreateTicketModal';

const SECTIONS_CONFIG = [
  { 
    id: 'upload',
    title: 'Загрузка календарей', 
    icon: <UploadFileIcon />, 
    color: '#1976d2', 
    path: '/upload',
    items: [
      { key: 'format', label: 'Формат: Excel (.xlsx, .xls)' },
      { key: 'columns', label: 'Колонки: Дата, Экскурсовод, Программа, Время' },
      { key: 'multiple-files', label: 'Загрузка нескольких файлов' },
      { key: 'duplicates', label: 'Удаление дубликатов' },
      { key: 'limit', label: 'До 10 000 записей' }
    ]
  },
  { 
    id: 'calendar',
    title: 'Календарь записей', 
    icon: <CalendarTodayIcon />, 
    color: '#2e7d32', 
    path: '/calendar',
    items: [
      { key: 'periods', label: 'Просмотр по периодам' },
      { key: 'sorting', label: 'Сортировка по столбцам' },
      { key: 'delete', label: 'Удаление записей' },
      { key: 'clear', label: 'Очистка периода' },
      { key: 'english', label: 'Отображение английского' }
    ]
  },
  { 
    id: 'acts',
    title: 'Генерация актов', 
    icon: <CalculateIcon />, 
    color: '#ed6c02', 
    path: '/calculation',
    items: [
      { key: 'select', label: 'Выбор гида и периода' },
      { key: 'calculate', label: 'Расчет сумм по программам' },
      { key: 'extra', label: 'Учет доп. программ' },
      { key: 'signatory', label: 'Подстановка подписанта' },
      { key: 'export', label: 'Экспорт в Excel' }
    ]
  },
  { 
    id: 'guides',
    title: 'Экскурсоводы', 
    icon: <PeopleIcon />, 
    color: '#9c27b0', 
    path: '/guides',
    items: [
      { key: 'add', label: 'Добавление/редактирование' },
      { key: 'contract', label: 'Указание контракта' },
      { key: 'active', label: 'Активация/деактивация' }
    ]
  },
  { 
    id: 'tours',
    title: 'Программы', 
    icon: <BookIcon />, 
    color: '#d32f2f', 
    path: '/tours',
    items: [
      { key: 'crud', label: 'Добавление/редактирование' },
      { key: 'prices', label: 'Настройка цен' },
      { key: 'active', label: 'Активация/деактивация' },
      { key: 'aliases', label: 'Привязка алиасов' }
    ]
  },
  { 
    id: 'aliases',
    title: 'Алиасы', 
    icon: <LabelIcon />, 
    color: '#0288d1', 
    path: '/aliases',
    items: [
      { key: 'mapping', label: 'Сопоставление названий' },
      { key: 'auto', label: 'Автозамена при загрузке' },
      { key: 'sort', label: 'Сортировка' }
    ]
  },
  { 
    id: 'extra',
    title: 'Доп. программы', 
    icon: <ExtensionIcon />, 
    color: '#6d4c41', 
    path: '/extra-programs',
    items: [
      { key: 'assign', label: 'Назначение гидам' },
      { key: 'hours', label: 'Указание часов и ставки' },
      { key: 'calc', label: 'Авторасчет суммы' }
    ]
  },
  { 
    id: 'settings',
    title: 'Настройки', 
    icon: <SettingsIcon />, 
    color: '#455a64', 
    path: '/settings',
    items: [
      { key: 'signatories', label: 'Настройка подписантов' },
      { key: 'active', label: 'Выбор активного' },
      { key: 'top', label: 'Вывод программ в Топе' }
    ]
  }
];

const Help: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);

  const [infoDialog, setInfoDialog] = useState({
    open: false,
    title: '',
    content: '',
    loading: false
  });

  const fetchItemContent = async (key: string) => {
    setInfoDialog({ ...infoDialog, loading: true, open: true });
    try {
      const response = await axios.get('/api/help/content/' + key);
      if (response.data.success) {
        setInfoDialog({
          open: true,
          title: SECTIONS_CONFIG.find(s => s.items.some(i => i.key === key))?.items.find(i => i.key === key)?.label || key,
          content: response.data.content,
          loading: false
        });
      } else {
        setInfoDialog({
          open: true,
          title: 'Информация',
          content: 'Контент временно недоступен',
          loading: false
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки:', error);
      setInfoDialog({
        open: true,
        title: 'Ошибка',
        content: 'Не удалось загрузить информацию',
        loading: false
      });
    }
  };

  const getGridSize = () => {
    if (isMobile) return { xs: 12 };
    if (isTablet) return { xs: 12, sm: 6 };
    return { xs: 12, sm: 6, md: 4, lg: 3 };
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, sm: 3, md: 4 }, maxWidth: '100%' }}>
      {/* Заголовок */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 4, bgcolor: 'primary.main', color: 'white', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <SchoolIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '2rem' } }}>
              Инструкция по работе с системой
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.8, fontSize: { xs: '0.8rem', sm: '1rem' } }}>
              Экскурсоводы 2.0 — руководство по функциям
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Вступление */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Добро пожаловать!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          Система управления <strong>Экскурсоводы 2.0</strong> создана для автоматизации работы с гидами, 
          программами и расчетами. Ниже представлены все основные разделы.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'primary.main' }}>
          💡 Нажмите на карточку для перехода в раздел. Нажмите на подпункт для подробной информации.
        </Typography>
      </Paper>

      {/* Карточки */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {SECTIONS_CONFIG.map((section, i) => (
          <Grid size={getGridSize()} key={i} sx={{ display: 'flex' }}>
            <Card 
              onClick={() => navigate(section.path)}
              sx={{ 
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                  borderColor: section.color,
                }
              }}
            >
              <CardContent sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                p: { xs: 2, sm: 2.5 }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5, 
                  mb: 1.5,
                }}>
                  <Box sx={{ color: section.color, display: 'flex', alignItems: 'center' }}>
                    {section.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    {section.title}
                  </Typography>
                </Box>
                
                <Divider sx={{ mb: 1.5 }} />
                
                <List dense disablePadding sx={{ flex: 1 }}>
                  {section.items.map((item, j) => (
                    <ListItem 
                      key={j} 
                      disablePadding 
                      sx={{ 
                        py: 0.4,
                        cursor: 'pointer',
                        borderRadius: 1,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: `${section.color}15`,
                          transform: 'translateX(4px)',
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        fetchItemContent(item.key);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 26 }}>
                        <InfoIcon sx={{ fontSize: 16, color: section.color }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.label} 
                        slotProps={{ 
                          primary: { 
                            variant: 'body2',
                            sx: { 
                              fontSize: { xs: '0.78rem', sm: '0.85rem' }, 
                              color: 'text.secondary',
                              lineHeight: 1.3
                            }
                          } 
                        }} 
                      />
                    </ListItem>
                  ))}
                </List>
                
                <Box sx={{ 
                  mt: 1.5, 
                  pt: 1, 
                  borderTop: '1px solid', 
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                    {section.items.length} подпунктов
                  </Typography>
                  <Typography variant="caption" color="primary" sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                    Перейти →
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Блок помощи */}
      <Paper sx={{ 
        p: { xs: 3, sm: 4 }, 
        mt: 4, 
        textAlign: 'center', 
        borderRadius: 2,
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: 'white'
      }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.2rem', sm: '1.8rem' } }}>
          Нужна помощь?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          Если у вас возникли вопросы или вы нашли ошибку, обратитесь к администратору системы.
        </Typography>
        <Button 
          variant="contained" 
          size="large"
          onClick={() => setOpenModal(true)}
          startIcon={<EmailIcon />}
          sx={{ 
            px: { xs: 3, sm: 4 }, 
            py: { xs: 1, sm: 1.5 },
            bgcolor: 'white',
            color: '#1976d2',
            borderRadius: 2,
            fontWeight: 'bold',
            fontSize: { xs: '0.85rem', sm: '1rem' },
            '&:hover': {
              bgcolor: '#f5f5f5',
              transform: 'scale(1.02)',
            },
            transition: 'all 0.2s ease'
          }}
        >
          Создать заявку
        </Button>
      </Paper>

      {/* Модальное окно заявки (переиспользуемое) */}
      <CreateTicketModal open={openModal} onClose={() => setOpenModal(false)} />

      {/* Диалог информации */}
      <Dialog 
        open={infoDialog.open} 
        onClose={() => setInfoDialog({ ...infoDialog, open: false })}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          p: 2
        }}>
          <Typography variant="h6" component="span">{infoDialog.title}</Typography>
          <IconButton onClick={() => setInfoDialog({ ...infoDialog, open: false })} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          {infoDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Container>Загрузка...</Container>
            </Box>
          ) : (
            <Box className="markdown-content" sx={{ 
              '& h1': { fontSize: '1.8rem', fontWeight: 'bold', mb: 2 },
              '& h2': { fontSize: '1.4rem', fontWeight: 'bold', mb: 1.5 },
              '& h3': { fontSize: '1.1rem', fontWeight: 'bold', mb: 1 },
              '& p': { mb: 1.5, lineHeight: 1.8 },
              '& ul': { pl: 2, mb: 1.5 },
              '& li': { mb: 0.5 },
              '& table': { width: '100%', borderCollapse: 'collapse', mb: 2, border: '1px solid #ddd' },
              '& th': { border: '1px solid #ddd', p: 1.5, textAlign: 'left', bgcolor: '#f5f5f5', fontWeight: 'bold' },
              '& td': { border: '1px solid #ddd', p: 1.5, textAlign: 'left' },
              '& code': { bgcolor: '#f5f5f5', p: 0.5, borderRadius: 1 },
              '& pre': { bgcolor: '#f5f5f5', p: 2, borderRadius: 1, overflow: 'auto' }
            }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {infoDialog.content}
              </ReactMarkdown>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialog({ ...infoDialog, open: false })} variant="contained">
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Help;
